import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  WhitebitWsBookTickerMessage,
  WhitebitWsDepthMessage,
  WhitebitWsKlineMessage,
  WhitebitWsLastPriceMessage,
  WhitebitWsRequest,
  WhitebitWsResponse,
  WhitebitWsTickerMessage,
  WhitebitWsTradesMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const WHITEBIT_WS_BASE_URL = 'wss://api.whitebit.com/ws';

/**
 * WhiteBIT WebSocket channel descriptors.
 * @see https://docs.whitebit.com/public/websocket/
 */
export const WHITEBIT_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'depth_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#market-depth',
  },
  ticker: {
    channel: 'ticker',
    topic: 'market_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#market-statistics',
  },
  klines: {
    channel: 'klines',
    topic: 'candles_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#kline',
  },
  trades: {
    channel: 'trades',
    topic: 'trades_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#market-trades',
  },
  lastPrice: {
    channel: 'lastPrice',
    topic: 'lastprice_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#last-price',
  },
  bookTicker: {
    channel: 'bookTicker',
    topic: 'bookTicker_subscribe',
    supportsBatching: true,
    docUrl: 'https://docs.whitebit.com/public/websocket/#book-ticker',
  },
};

/**
 * WhiteBIT WebSocket topology information.
 * JSON-RPC protocol with plain JSON (no compression).
 * Client-initiated ping every 50 seconds.
 * Incremental orderbook with update_id sequence tracking.
 * @see https://docs.whitebit.com/public/websocket/
 */
export const WHITEBIT_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: WHITEBIT_WS_BASE_URL,
  singleConnectionPerChannel: false, // Multiplexed WebSocket
  heartbeatIntervalMs: 50_000, // Client sends ping every 50 seconds
  note: 'JSON-RPC protocol. Plain JSON (no compression). Client-initiated ping every 50 seconds. Incremental orderbook with update_id sequence tracking. Symbol format: uppercase underscore (BTC_USDT).',
};

// ========================================
// Helper Types and Constants
// ========================================

export type WhitebitWsStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

/**
 * WhiteBIT supported kline intervals (in seconds).
 * Matches REST API intervals: 1m, 3m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 8h, 12h, 1d, 3d, 1w, 1M.
 * @see https://docs.whitebit.com/public/http-v1/#kline
 * @see https://docs.whitebit.com/public/websocket/#kline
 */
export type WhitebitKlineInterval =
  | 60 // 1m
  | 180 // 3m
  | 300 // 5m
  | 900 // 15m
  | 1800 // 30m
  | 3600 // 1h
  | 7200 // 2h
  | 14400 // 4h
  | 21600 // 6h
  | 28800 // 8h
  | 43200 // 12h
  | 86400 // 1d
  | 259200 // 3d
  | 604800 // 1w
  | 2592000; // 1M

// ========================================
// Message Type Guards
// ========================================

/**
 * Check if message is a ping response (pong from server).
 */
export function isWhitebitWsPong(data: unknown): data is WhitebitWsResponse {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as WhitebitWsResponse;
  return candidate.result === 'pong';
}

/**
 * Check if message is a depth update.
 */
export function isWhitebitWsDepthMessage(data: unknown): data is WhitebitWsDepthMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'depth_update';
}

/**
 * Check if message is a trades update.
 */
export function isWhitebitWsTradesMessage(data: unknown): data is WhitebitWsTradesMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'trades_update';
}

/**
 * Check if message is a kline (candles) update.
 */
export function isWhitebitWsKlineMessage(data: unknown): data is WhitebitWsKlineMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'candles_update';
}

/**
 * Check if message is a market (ticker) update.
 */
export function isWhitebitWsTickerMessage(data: unknown): data is WhitebitWsTickerMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'market_update';
}

/**
 * Check if message is a last price update.
 */
export function isWhitebitWsLastPriceMessage(data: unknown): data is WhitebitWsLastPriceMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'lastprice_update';
}

/**
 * Check if message is a book ticker update.
 */
export function isWhitebitWsBookTickerMessage(data: unknown): data is WhitebitWsBookTickerMessage {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return candidate.method === 'bookTicker_update';
}

// ========================================
// Subscription Request Builders
// ========================================

let requestIdCounter = 1;

/**
 * Generate unique request ID.
 */
function generateRequestId(): number {
  return requestIdCounter++;
}

/**
 * Create a ping request (client-initiated heartbeat).
 */
export function createWhitebitPing(): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'ping',
    params: [],
  };
}

/**
 * Create depth subscription request.
 * @param market Trading pair (e.g., "BTC_USDT")
 * @param limit Number of levels (default: 100)
 * @param priceInterval Price aggregation interval (default: "0" for best precision)
 */
export function createWhitebitDepthSubscription(
  market: string,
  limit: number = 100,
  priceInterval: string = '0',
): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'depth_subscribe',
    params: [market, limit, priceInterval, true], // true = enable delta updates
  };
}

/**
 * Create market (ticker) subscription request.
 * @param markets Array of trading pairs (e.g., ["BTC_USDT", "ETH_USDT"])
 */
export function createWhitebitTickerSubscription(markets: string[]): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'market_subscribe',
    params: markets,
  };
}

/**
 * Create trades subscription request.
 * @param markets Array of trading pairs (e.g., ["BTC_USDT", "ETH_USDT"])
 */
export function createWhitebitTradesSubscription(markets: string[]): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'trades_subscribe',
    params: markets,
  };
}

/**
 * Create kline (candles) subscription request.
 * @param market Trading pair (e.g., "BTC_USDT")
 * @param interval Interval in seconds (e.g., 60 for 1m, 3600 for 1h)
 */
export function createWhitebitKlineSubscription(
  market: string,
  interval: WhitebitKlineInterval,
): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'candles_subscribe',
    params: [market, interval],
  };
}

/**
 * Create last price subscription request.
 * @param markets Array of trading pairs (e.g., ["BTC_USDT", "ETH_USDT"])
 */
export function createWhitebitLastPriceSubscription(markets: string[]): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'lastprice_subscribe',
    params: markets,
  };
}

/**
 * Create book ticker subscription request.
 * @param markets Array of trading pairs (e.g., ["BTC_USDT", "ETH_USDT"])
 */
export function createWhitebitBookTickerSubscription(markets: string[]): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'bookTicker_subscribe',
    params: markets,
  };
}

// ========================================
// Unsubscription Request Builders
// ========================================

/**
 * Create depth unsubscription request.
 */
export function createWhitebitDepthUnsubscription(): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'depth_unsubscribe',
    params: [],
  };
}

/**
 * Create market (ticker) unsubscription request.
 */
export function createWhitebitTickerUnsubscription(): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'market_unsubscribe',
    params: [],
  };
}

/**
 * Create trades unsubscription request.
 */
export function createWhitebitTradesUnsubscription(): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'trades_unsubscribe',
    params: [],
  };
}

/**
 * Create kline (candles) unsubscription request.
 */
export function createWhitebitKlineUnsubscription(): WhitebitWsRequest {
  return {
    id: generateRequestId(),
    method: 'candles_unsubscribe',
    params: [],
  };
}

// ========================================
// WebSocket Subscription Options
// ========================================

export interface WhitebitWsSubscriptionOptions<T> {
  /** Subscription request message */
  subscribeMessage: WhitebitWsRequest;
  /** Type guard function to filter relevant messages */
  messageFilter: (data: unknown) => data is T;
  /** Callback for successfully decoded messages */
  onMessage: (data: T) => void;
  /** Callback for errors (connection, decode, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
  /** Base WebSocket URL (defaults to WHITEBIT_WS_BASE_URL) */
  baseUrl?: string;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

// ========================================
// Generic WebSocket Subscription
// ========================================

/**
 * Create a generic WhiteBIT WebSocket subscription.
 * Handles JSON-RPC protocol, ping/pong heartbeat, and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 */
function createWhitebitWsSubscription<T>(
  options: WhitebitWsSubscriptionOptions<T>,
): ReconnectingWebSocket {
  const {
    subscribeMessage,
    messageFilter,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = WHITEBIT_WS_BASE_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Send subscription request
    ws.send(JSON.stringify(subscribeMessage));

    // Start client-initiated heartbeat (ping every 50 seconds)
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(createWhitebitPing()));
      }
    }, WHITEBIT_WS_TOPOLOGY.heartbeatIntervalMs);
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      const data = event.data as string;

      // Parse JSON message
      const payload = JSON.parse(data) as unknown;

      // Handle pong response (no action needed)
      if (isWhitebitWsPong(payload)) {
        return;
      }

      // Handle subscription response (has id field)
      const response = payload as WhitebitWsResponse;
      if (response.id !== null && response.id !== undefined) {
        // Check for subscription error
        if (response.error) {
          const err = response.error as { code: number; message: string };
          onError?.(new Error(`WhiteBIT subscription error: ${err.code} - ${err.message}`));
        }
        // Subscription confirmed (success or error handled) - don't pass to onMessage
        return;
      }

      // Handle data message using type guard
      if (messageFilter(payload)) {
        onMessage(payload);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`WhiteBIT WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  // Cleanup on close
  const originalClose = ws.close.bind(ws);
  ws.close = (code?: number, reason?: string) => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    originalClose(code, reason);
  };

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface WhitebitTickerSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Callback for ticker updates */
  onMessage: (ticker: WhitebitWsTickerMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT ticker (market statistics) updates via WebSocket.
 *
 * @see https://docs.whitebit.com/public/websocket/#market-statistics
 */
export function subscribeWhitebitTicker(
  options: WhitebitTickerSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsTickerMessage>({
    subscribeMessage: createWhitebitTickerSubscription([symbol]),
    messageFilter: isWhitebitWsTickerMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Kline (Candlestick) Subscription
// ========================================

export interface WhitebitKlineSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Kline interval in seconds */
  interval: WhitebitKlineInterval;
  /** Callback for kline updates */
  onMessage: (kline: WhitebitWsKlineMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT kline (candlestick) updates via WebSocket.
 * IMPORTANT: Kline timestamps are in SECONDS (must multiply by 1000).
 *
 * @see https://docs.whitebit.com/public/websocket/#kline
 */
export function subscribeWhitebitKlines(
  options: WhitebitKlineSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsKlineMessage>({
    subscribeMessage: createWhitebitKlineSubscription(symbol, interval),
    messageFilter: isWhitebitWsKlineMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Depth (Orderbook) Subscription
// ========================================

export interface WhitebitDepthSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Number of levels (default: 100, max: 100) */
  limit?: number;
  /** Price aggregation interval (default: "0" for best precision) */
  priceInterval?: string;
  /** Callback for depth updates */
  onMessage: (depth: WhitebitWsDepthMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT depth (orderbook) updates via WebSocket.
 * IMPORTANT: First message is a snapshot (no past_update_id), subsequent are deltas.
 * Use update_id and past_update_id for sequence tracking.
 *
 * @see https://docs.whitebit.com/public/websocket/#market-depth
 */
export function subscribeWhitebitDepth(
  options: WhitebitDepthSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, limit = 100, priceInterval = '0', onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsDepthMessage>({
    subscribeMessage: createWhitebitDepthSubscription(symbol, limit, priceInterval),
    messageFilter: isWhitebitWsDepthMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Trade Subscription
// ========================================

export interface WhitebitTradeSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Callback for trade updates */
  onMessage: (trades: WhitebitWsTradesMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT public trade updates via WebSocket.
 *
 * @see https://docs.whitebit.com/public/websocket/#market-trades
 */
export function subscribeWhitebitTrades(
  options: WhitebitTradeSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsTradesMessage>({
    subscribeMessage: createWhitebitTradesSubscription([symbol]),
    messageFilter: isWhitebitWsTradesMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Last Price Subscription
// ========================================

export interface WhitebitLastPriceSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Callback for last price updates */
  onMessage: (lastPrice: WhitebitWsLastPriceMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT last price updates via WebSocket.
 *
 * @see https://docs.whitebit.com/public/websocket/#last-price
 */
export function subscribeWhitebitLastPrice(
  options: WhitebitLastPriceSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsLastPriceMessage>({
    subscribeMessage: createWhitebitLastPriceSubscription([symbol]),
    messageFilter: isWhitebitWsLastPriceMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Book Ticker (Best Bid/Ask) Subscription
// ========================================

export interface WhitebitBookTickerSubscriptionOptions {
  /** Uppercase symbol with underscore (e.g., "BTC_USDT") */
  symbol: string;
  /** Callback for book ticker updates */
  onMessage: (bookTicker: WhitebitWsBookTickerMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: WhitebitWsStatus) => void;
}

/**
 * Subscribe to WhiteBIT book ticker (best bid/ask) updates via WebSocket.
 *
 * @see https://docs.whitebit.com/public/websocket/#book-ticker
 */
export function subscribeWhitebitBookTicker(
  options: WhitebitBookTickerSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createWhitebitWsSubscription<WhitebitWsBookTickerMessage>({
    subscribeMessage: createWhitebitBookTickerSubscription([symbol]),
    messageFilter: isWhitebitWsBookTickerMessage,
    onMessage,
    onError,
    onStatusChange,
  });
}
